// Cliente de la pestaña "Cobro" del HCE (ver packages/web/src/api/http-
// routes/hce-cobro.ts) — mismo Bearer (sesión de proxy) que el resto de
// `/api/hce/*`, análogo a farmaciaInterna.ts. Solo arma y envía la orden de
// la atención en curso; COBRAR (perfiles de facturación, forma de pago,
// vuelto) vive en /admin/cobrar del CRM, no acá.
import { vaultUrl } from './hceVault';
import { cabecerasBoveda } from './audit/auditedFetch';

async function post<T>(path: string, body: unknown): Promise<{ ok: true; datos: T } | { ok: false; error: string; datos?: Record<string, unknown> }> {
  const res = await fetch(vaultUrl(path), { method: 'POST', headers: cabecerasBoveda(), body: JSON.stringify(body) });
  const datos = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: datos?.error || 'No se pudo completar la operación.', datos };
  return { ok: true, datos };
}

async function put<T>(path: string, body: unknown): Promise<{ ok: true; datos: T } | { ok: false; error: string }> {
  const res = await fetch(vaultUrl(path), { method: 'PUT', headers: cabecerasBoveda(), body: JSON.stringify(body) });
  const datos = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: datos?.error || 'No se pudo completar la operación.' };
  return { ok: true, datos };
}

async function get<T>(path: string, porDefecto: T): Promise<T> {
  const res = await fetch(vaultUrl(path), { headers: cabecerasBoveda() });
  if (!res.ok) return porDefecto;
  return res.json().catch(() => porDefecto);
}

export type CatalogoItemCobro = {
  id: number;
  code: string;
  name: string;
  kind: 'procedure' | 'package' | 'supply' | 'drug';
  amountCents: number;
  active: boolean;
  /** Solo para `kind` supply/drug (ítem 11) — usable menos reservado por otras órdenes. `null` en procedure/package (no llevan stock). */
  disponible: number | null;
  /** Botiquín de servicio (Fase 2, ítem 12) — no-null cuando este ítem es el espejo de un ítem real de Farmacia Interna. Una línea de este ítem exige registro de administración (ítem 12b) antes de "Enviar a caja". */
  farmaciaItemId: number | null;
};

/** `disponible: false` distingue "catálogo vacío" de "no se pudo consultar" (sesión vencida, backend caído) — el llamador debe avisar en el segundo caso, no mostrar "sin prestaciones" en silencio. `orderId`, si se pasa, excluye el apartado de ESA orden al calcular `disponible` por ítem (ítem 11) — para que reabrir un carrito en edición no se autobloquee con su propio apartado. */
export async function listarCatalogoCobro(specialtyId?: string, orderId?: number): Promise<{ disponible: boolean; items: CatalogoItemCobro[] }> {
  const params = new URLSearchParams();
  if (specialtyId) params.set('specialtyId', specialtyId);
  if (orderId) params.set('orderId', String(orderId));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const body = await get<{ disponible: boolean; items?: CatalogoItemCobro[] }>(`/api/hce/cobro/catalogo${qs}`, { disponible: false, items: [] });
  return { disponible: body.disponible, items: Array.isArray(body.items) ? body.items : [] };
}

export type LineaAtencionCobro = {
  id: number;
  itemId: number;
  description: string;
  quantity: number;
  unitAmountCents: number;
  lineTotalCents: number;
  catalogPriceCents: number | null;
  discountPct: number | null;
  discountReason: string | null;
  discountAppliedBy: string | null;
  /** Sugerencia NO binding del médico (facilitaclinic/multiples-sedes, Fase 2 ítem 9b) — recepción la ve al cobrar en /admin/cobrar. */
  suggestedDiscountPct: number | null;
  suggestedDiscountNote: string | null;
  /** Registro de administración del botiquín de servicio (Fase 2, ítem 12b) — obligatorio antes de "Enviar a caja" si el ítem es del botiquín. */
  administracionDosis: string | null;
  administracionVia: string | null;
  administracionLotCode: string | null;
  medicationAdministrationRef: string | null;
  /** `consulta_cita` = la línea de consulta que el SERVIDOR precargó desde la cita de la Sala (Sala CRM↔HCE 5b): no se borra ni se edita desde el HCE. `null`/ausente = línea agregada a mano. */
  origenLinea?: 'consulta_cita' | null;
};
export type EstadoAtencionCobro = 'draft' | 'signed' | 'invoiced' | 'cancelled';
export type OrdenAtencionCobro = {
  id: number;
  patientId: number | null;
  status: EstadoAtencionCobro;
  signedAt: string | null;
  createdAt: string;
  totalCents: number;
  lines: LineaAtencionCobro[];
  invoiceId?: number | null;
  /** La cita a la que el servidor ligó esta atención al precargar la consulta (solo informativo: el cliente NUNCA la envía). */
  citaId?: string | null;
};

/** Resultado de la precarga de la consulta de la cita (Sala CRM↔HCE 5b). Solo `error` se le muestra al médico; el resto es silencioso (sin cita, con cobro, sin servicio de consulta que precargar —`sin_servicio` con motivo `sin_configurar`, `sin_precio`, `item_invalido` o `ambigua`—…). */
export type PrecargaCobro = { estado: string; motivo?: string };

export async function crearOObtenerAtencion(input: { patientId: number; patientPhone?: string; encounterId?: string; specialtyId?: string }) {
  return post<{ orden: OrdenAtencionCobro; precarga?: PrecargaCobro }>('/api/hce/cobro/atencion', input);
}

export type LineaAtencionInput = {
  itemId: number;
  quantity: number;
  /** Solo tiene efecto en la familia "demo" (Fase 2, ítem 9) — el server lo revalida. */
  unitAmountCentsOverride?: number;
  discountPct?: number;
  discountReason?: string;
  /** Sugerencia NO binding (facilitaclinic/multiples-sedes, Fase 2 ítem 9b) — sin tope, sin motivo obligatorio. */
  suggestedDiscountPct?: number;
  suggestedDiscountNote?: string;
  /** Botiquín de servicio (Fase 2, ítem 12b) — sin exigir al Guardar, solo al "Enviar a caja" (ver `enviarAtencionACaja`, que el server valida). */
  administracionDosis?: string | null;
  administracionVia?: string | null;
  administracionLotCode?: string | null;
};

export async function actualizarLineasAtencion(orderId: number, lines: LineaAtencionInput[], specialtyId?: string) {
  return put<{ totalCents: number; orden: OrdenAtencionCobro }>(`/api/hce/cobro/atencion/${orderId}/lineas`, { lines, specialtyId });
}

/** Lotes usable del ítem YA espejo del botiquín (ítem 12b) — para que el médico elija de una lista el lote que administró, mismo patrón que "Reponer botiquín" (ítem 12a). Carga perezosa: se llama solo cuando el médico abre el selector de una línea de botiquín. */
export async function listarLotesDelEspejo(itemId: number): Promise<{ id: number; lotCode: string; expiresAt: string | null; quantity: number }[]> {
  const body = await get<{ disponible: boolean; lotes?: { id: number; lotCode: string; expiresAt: string | null; quantity: number }[] }>(
    `/api/hce/cobro/catalogo/${itemId}/lotes`,
    { disponible: false, lotes: [] },
  );
  return Array.isArray(body.lotes) ? body.lotes : [];
}

/** `salaCerrada`: `true` el turno de la cita pasó a atendido; `false` ya estaba cerrado; `null` la orden no está ligada a una cita; `'error'` la orden se envió pero NO se pudo cerrar el turno (recepción lo cierra a mano). `yaEnviada`: reintento de una orden ya enviada. */
export type RespuestaEnvio = { ok: true; salaCerrada?: boolean | 'error' | null; yaEnviada?: boolean };

export async function enviarAtencionACaja(orderId: number) {
  return post<RespuestaEnvio>(`/api/hce/cobro/atencion/${orderId}/enviar`, {});
}

/** "Enviar a caja" firma lo GUARDADO en el servidor, no el carrito de pantalla (Q41/H4): primero se guarda el carrito completo (PUT líneas) y solo si eso sale bien se envía (POST /enviar). Se detiene en el primer error, con su mensaje en español. */
export async function guardarYEnviarACaja(
  orderId: number,
  lineas: LineaAtencionInput[],
  specialtyId?: string,
): Promise<{ ok: true; salaCerrada?: boolean | 'error' | null; yaEnviada?: boolean } | { ok: false; error: string; recargar?: boolean }> {
  let guardado: Awaited<ReturnType<typeof actualizarLineasAtencion>>;
  try {
    guardado = await actualizarLineasAtencion(orderId, lineas, specialtyId);
  } catch {
    return { ok: false, error: 'No se pudo guardar el carrito antes de enviarlo a caja. Revise su conexión e intente de nuevo.' };
  }
  if (!guardado.ok) return { ok: false, error: guardado.error };
  try {
    const enviado = await enviarAtencionACaja(orderId);
    if (!enviado.ok) {
      // El servidor agregó o soltó la consulta de la cita y NO firmó (responde `precarga`): la pantalla vuelve a cargar la atención sola y muestra el total nuevo.
      return { ok: false, error: enviado.error, ...(enviado.datos?.precarga ? { recargar: true } : {}) };
    }
    return { ok: true, salaCerrada: enviado.datos.salaCerrada, yaEnviada: enviado.datos.yaEnviada };
  } catch {
    return { ok: false, error: 'El carrito se guardó, pero no se pudo enviar a caja. Revise su conexión e intente de nuevo.' };
  }
}

export type CotizacionCobro = {
  totalCents: number;
  subtotalCents: number;
  ivaCents: number;
  grupos: { ivaRate: number; baseCents: number; ivaCents: number }[];
};

/** Cotiza el carrito de pantalla en el servidor (Q41) con la función única de impuestos; `null` si falla (la pantalla muestra "estimado"). */
export async function cotizarCarrito(lineas: LineaAtencionInput[], specialtyId?: string): Promise<CotizacionCobro | null> {
  try {
    const r = await post<CotizacionCobro>('/api/hce/cobro/cotizar', { lines: lineas, specialtyId });
    return r.ok ? r.datos : null;
  } catch {
    return null;
  }
}

export async function anularAtencion(orderId: number, reason: string) {
  return post<{ ok: true }>(`/api/hce/cobro/atencion/${orderId}/anular`, { reason });
}

export async function listarOrdenesDeCobroDePaciente(patientId: number): Promise<{ disponible: boolean; ordenes: OrdenAtencionCobro[] }> {
  return get(`/api/hce/cobro/paciente/${patientId}/ordenes`, { disponible: false, ordenes: [] });
}

// ---------------------------------------------------------------------------
// Consulta precargada de la cita (Sala CRM↔HCE 5b, PLAN C9)
// ---------------------------------------------------------------------------

export const ORIGEN_CONSULTA_CITA = 'consulta_cita' as const;

/** La línea de consulta que el servidor precargó en la atención, o `null` si no la lleva. Sale de la ORDEN guardada, nunca del carrito de pantalla. */
export function lineaConsultaBloqueada(orden: Pick<OrdenAtencionCobro, 'lines'> | null | undefined): LineaAtencionCobro | null {
  return orden?.lines.find((l) => l.origenLinea === ORIGEN_CONSULTA_CITA) ?? null;
}

/**
 * Las líneas que viajan al servidor (guardar, enviar, cotizar): la consulta precargada IDÉNTICA (una entrada, cantidad 1) más las del carrito. El
 * servidor rechaza el carrito que no la traiga tal cual, así que nunca se omite. `conPrecio` (familia «demo», que siempre manda el precio visible)
 * la manda con su precio congelado; las demás familias no mandan precio.
 */
export function conConsultaPrecargada(orden: Pick<OrdenAtencionCobro, 'lines'> | null | undefined, lineas: LineaAtencionInput[], conPrecio = false): LineaAtencionInput[] {
  const consulta = lineaConsultaBloqueada(orden);
  if (!consulta) return lineas;
  const entrada: LineaAtencionInput = { itemId: consulta.itemId, quantity: 1, ...(conPrecio ? { unitAmountCentsOverride: consulta.unitAmountCents } : {}) };
  return [entrada, ...lineas.filter((l) => l.itemId !== consulta.itemId)];
}

/** `true` si hay que decirle al médico que la precarga falló (queda el carrito vacío; recepción puede agregar la consulta al cobrar). */
export function precargaFallo(precarga: PrecargaCobro | undefined): boolean {
  return precarga?.estado === 'error';
}
